import { useMutation } from '@tanstack/react-query';
import { Button } from '~/components/primitives';
import { DropdownMenuItem } from '~/components/ui/dropdown-menu';
import useNotifications from '~/features/notifications/useNotifications';

export function useMutationWithNotifications<T>({
    mutationFn,
    onSuccess,
    onSuccessMessage,
    onError,
    onErrorMessage,
}: {
    mutationFn: () => Promise<T>;
    onSuccess?(data: NoInfer<T>): void;
    onSuccessMessage?: string;
    onError?(error: Error): void;
    onErrorMessage?: string;
}) {
    const notifications = useNotifications();

    return useMutation({
        mutationFn,
        throwOnError: false,

        onSuccess(data) {
            notifications.dispatchSuccess({ message: onSuccessMessage });
            onSuccess?.(data);
        },
        onError(e) {
            notifications.dispatchError({ message: onErrorMessage });
            if (onErrorMessage) {
                console.error(onErrorMessage, e);
            } else {
                console.error(e);
            }
            onError?.(e);
        },
    });
}

export const MutationButton = <T,>({
    mutationFn,
    onSuccess,
    onSuccessMessage,
    onErrorMessage,
    onError,
    startTransition = cb => cb(),
    ...btnProps
}: Button.Props & {
    mutationFn: () => Promise<T>;

    startTransition?: React.TransitionStartFunction;

    onSuccess?(data: NoInfer<T>): void;
    onSuccessMessage?: string;
    onError?(error: Error): void;
    onErrorMessage?: string;
}): React.ReactNode => {
    const mutation = useMutationWithNotifications({
        mutationFn,
        onSuccess,
        onSuccessMessage,
        onError,
        onErrorMessage,
    });

    return (
        <Button
            {...btnProps}
            disabled={mutation.isPending || btnProps.disabled}
            isLoading={mutation.isPending || btnProps.isLoading}
            onClick={async e => {
                await btnProps.onClick?.(e);
                // Allow onClick to prevent mutation
                if (e.isDefaultPrevented()) {
                    return;
                }

                return await new Promise(resolve => {
                    startTransition(async () => {
                        const result = await mutation.mutateAsync();
                        resolve(result);
                    });
                });
            }}
        />
    );
};

export const MutationDropdownItem = <T,>({
    mutationFn,
    onSuccess,
    onSuccessMessage,
    onErrorMessage,
    onError,
    startTransition = cb => cb(),
    children,
    loadingText,
    ...itemProps
}: React.ComponentProps<typeof DropdownMenuItem> & {
    mutationFn: () => Promise<T>;

    startTransition?: React.TransitionStartFunction;

    onSuccess?(data: NoInfer<T>): void;
    onSuccessMessage?: string;
    onError?(error: Error): void;
    onErrorMessage?: string;

    children: React.ReactNode;
    loadingText?: string;
}): React.ReactNode => {
    const mutation = useMutationWithNotifications({
        mutationFn,
        onSuccess,
        onSuccessMessage,
        onError,
        onErrorMessage,
    });

    return (
        <DropdownMenuItem
            {...itemProps}
            disabled={mutation.isPending || itemProps.disabled}
            onSelect={event => {
                event.preventDefault();
                itemProps.onSelect?.(event);
            }}
            onClick={async event => {
                if (itemProps.onClick) {
                    try {
                        await itemProps.onClick(event);
                    } catch (error) {
                        console.error('Error in onClick handler:', error);
                        throw error;
                    }
                }
                if (event.defaultPrevented) {
                    return;
                }

                await new Promise(resolve => {
                    startTransition(async () => {
                        const result = await mutation.mutateAsync();
                        // Close dropdown after successful mutation
                        itemProps.onSelect?.(event as unknown as Event);
                        resolve(result);
                    });
                });
            }}
        >
            {mutation.isPending && loadingText ? loadingText : children}
        </DropdownMenuItem>
    );
};
